import { tintRgbTohex } from "components/ColorItem/ColorItem"
import { print } from "graphql"
import queryClient from "providers/queryClient"
import { clearBillingState } from "store/billing/billingSlice"
import { clearProductSlice } from "store/product/productSlice"
import { store } from "store/store"
import { clearUserInfo, userExpired } from "store/user/userSlice"
import {
  type Brand,
  type Cover,
  type GlassesDraftListItem,
  type GlassesEditorDto,
  type GlassesEditorInitialSku,
  type GlassesLimits,
  IntegrationType,
  type MerchantData,
  type MerchantId,
  type ProductV2,
  type UserData,
} from "types"
import { clearTiendanubeToken } from "utils/authSessionStorage"
import { parseAwsTokenToObject } from "utils/helper"
import { convertGlassesZipToObject } from "utils/zipEffectFileReader"
import { typedEnv } from "../config/typedEnv"
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  InMemoryCache,
  type NormalizedCacheObject,
  Observable,
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { type ErrorResponse, onError } from "@apollo/client/link/error"
import { Sha256 } from "@aws-crypto/sha256-js"
import { HttpRequest } from "@aws-sdk/protocol-http"
import { SignatureV4 } from "@aws-sdk/signature-v4"
import { adminApi } from "./adminApi"
import { getClerkAppsyncToken, resolveServiceEndpoint } from "./platformApiAuth"
import * as schema from "./schema.graphql"
import { type ProductType } from "store/product/types"
import { type ProductCategories } from "components/Navbar/types"

interface IAWSToken {
  awsAccessKey: string
  awsJwtToken: string
  awsSecretAccessKey: string
}

type ClerkWindow = Window & {
  Clerk?: {
    signOut?: (options?: { redirectUrl?: string }) => Promise<void>
  }
}

type ProductTypesQueryResult = {
  data?: {
    types?: ProductType[]
  }
}

type LoginAccessResponse = {
  data: {
    groups: string[]
    merchantId: string
    status?: string
    username: string
  }
  token: string
}

type ProductByIdSku = {
  externalId?: string | null
  id: string
  name: string
  sku: string
  upc: string
  url: string
  vto: {
    color: string
    coverage: string
    effectUrl?: string
    finish: string
    region: string
  }
}

type ProductByIdResult = {
  productV2: {
    brand: string
    brandSlug: string
    category: {
      id: number
    }
    cover: string
    externalId?: string | null
    id: string
    merchant?: string
    name: string
    skus: ProductByIdSku[]
    type: string
    visible?: boolean
  }
}

export class TintVTOService {
  endpoint: string = `https://${typedEnv.VITE_ADMIN_ENDPOINT}/graphql`
  legacyBillingEndpoint: string = resolveServiceEndpoint(
    typedEnv.VITE_BILLING_TINTVTO_SERVICE_ENDPOINT
  )
  apiKey: string = typedEnv.VITE_ADMIN_API_KEY
  userType: IntegrationType = IntegrationType.COMMON
  awsToken: IAWSToken | null = null
  gqlClient!: ApolloClient<NormalizedCacheObject>

  constructor(userType: IntegrationType = IntegrationType.COMMON) {
    this.userType = userType
    this.updateGQLClient()
  }

  async getClerkAppsyncToken() {
    return getClerkAppsyncToken()
  }

  changeUserType(userT: IntegrationType) {
    this.userType = userT
    this.updateGQLClient()
  }

  changeAwsToken(token: IAWSToken | null) {
    this.awsToken = token
    this.updateGQLClient()
  }

  changeAwsTokenAndUserType(token: string | null, userT: IntegrationType) {
    if (token) this.awsToken = parseAwsTokenToObject(token) ?? null
    else this.awsToken = null

    this.changeUserType(userT)
    this.updateGQLClient()
  }

  updateGQLClient() {
    const httpLink = createHttpLink({
      uri: this.endpoint,
    })

    const logoutLink = onError(({ networkError }: ErrorResponse): void => {
      if (networkError && "statusCode" in networkError) {
        if (
          (networkError.statusCode === 403 || networkError.statusCode === 401) &&
          this.userType !== IntegrationType.COMMON
        ) {
          this.logoutUserExpired()
          return
        }
        if (this.userType === IntegrationType.COMMON && networkError.statusCode === 401) {
          this.gqlClient.stop()
          this.logout()
          return
        }
      }
    })

    const authLink = this.createAuthLink()

    this.gqlClient = new ApolloClient({
      link: ApolloLink.from([logoutLink, authLink, httpLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "no-cache",
          errorPolicy: "ignore",
        },
        query: {
          fetchPolicy: "no-cache",
          errorPolicy: "all",
        },
      },
    })
  }

  createSignedAWSRequest = async (
    uri: string,
    operationName: string,
    query: string,
    variables: Record<string, string> = {}
  ) => {
    const url = new URL(uri)
    const requestUri = url.href.includes("/graphql") ? url.href : `${url.origin}/graphql`
    const hostname = url.hostname
    const path = url.pathname.includes("/graphql") ? url.pathname : "/graphql"

    const request = new HttpRequest({
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        host: hostname,
      },
      body: JSON.stringify({ operationName, query, variables }),
      hostname: hostname,
      path: path,
    })

    const signer = new SignatureV4({
      credentials: {
        accessKeyId: this.awsToken!.awsAccessKey,
        secretAccessKey: this.awsToken!.awsSecretAccessKey,
        sessionToken: this.awsToken!.awsJwtToken,
      },
      region: "us-east-1",
      service: "appsync",
      sha256: Sha256,
    })

    const signedRequest = await signer.sign(request)

    return new Request(requestUri, {
      method: signedRequest.method,
      headers: signedRequest.headers,
      body: signedRequest.body,
    })
  }

  createAuthLink(): ApolloLink {
    if (this.userType === IntegrationType.TIENDANUBE)
      return new ApolloLink((operation) => {
        return new Observable((observer) => {
          ;(async () => {
            try {
              const { operationName, query, variables } = operation
              const operationString = print(query)

              const signedRequest = await this.createSignedAWSRequest(
                this.endpoint,
                operationName,
                operationString || "",
                variables
              )
              const response = await fetch(signedRequest)
              if (!response.ok) {
                const error = new Error(`HTTP error: ${response.status}`) as any
                error.statusCode = response.status
                error.response = response
                throw error
              }

              const result = await response.json()
              observer.next(result)
              observer.complete()
            } catch (error) {
              observer.error(error)
              throw error
            }
          })()
        })
      })
    else
      return setContext(async ({ operationName }, { headers }) => {
        const isLoginOperation = operationName === "loginFromMiddle"
        const token = isLoginOperation ? null : await this.getClerkAppsyncToken()

        return {
          headers: {
            ...headers,
            ...this.getHeaders(isLoginOperation, token),
          },
        }
      })
  }

  getHeaders(login: boolean, token?: string | null) {
    if (login)
      return {
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-api-key": this.apiKey,
      }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    }

    if (token) headers.Authorization = token

    return headers
  }

  async authorizeCDN() {
    if (this.userType === IntegrationType.COMMON) {
      const response = await adminApi.authorizeCdn()
      return {
        data: {
          authorizeCDN: response.jwt,
        },
      }
    }

    return await this.gqlClient.query({
      query: schema.AuthorizeCDNQuery,
    })
  }

  async uploadCDN(params: { body: BodyInit; name: string; type: string; jwt: string }) {
    try {
      await fetch(`https://${typedEnv.VITE_CDN_UPLOAD_ENDPOINT}/${params.name}?jwt=${params.jwt}`, {
        method: "PUT",
        headers: {
          "Content-Type": params.type,
        },
        body: params.body,
      })
    } catch (e) {
      console.log(e)
    }
    return `https://${typedEnv.VITE_CDN_ENDPOINT}/${params.name}`
  }

  async deleteCDN(params: { name: string; jwt: string }) {
    try {
      await fetch(`https://${typedEnv.VITE_CDN_UPLOAD_ENDPOINT}/${params.name}?jwt=${params.jwt}`, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "DELETE",
      })
    } catch (e) {
      console.log(e)
    }
  }

  async getMerchantCdnConfig(merchantId: string) {
    try {
      const response = await fetch(
        `https://cdn.tintvto.sdk.banuba.net/merchants/${merchantId}/config.json`
      )

      const data = await response.json()
      const config = JSON.parse(window.atob(data.config) ?? "")

      return config
    } catch (e) {
      return null
    }
  }

  async types(): Promise<ProductTypesQueryResult> {
    if (this.userType === IntegrationType.COMMON) {
      const response = await adminApi.listProductTypes()

      return {
        data: {
          types: response.items,
        },
      }
    }

    return await this.gqlClient.query({
      query: schema.ProductTypesQuery,
    })
  }

  async categories() {
    return await this.gqlClient.query({
      query: schema.ProductCategoriesQuery,
    })
  }

  async saveMerchant(merchant: MerchantData) {
    const merchantId = merchant.id ? merchant.id : ""

    return await this.gqlClient.mutate({
      mutation: schema.SaveMerchantMutation,
      variables: {
        merchantId,
        name: merchant.name,
        email: merchant.email,
        domain: merchant.domain,
        planId: merchant.planId,
        cognitoSub: merchant.cognitoSub,
        description: merchant.description,
        brandsIds: merchant.brandsIds,
        token: merchant.token ? merchant.token : "",
      },
    })
  }

  async registerUser({ username, password, group }: UserData) {
    return await this.gqlClient.mutate({
      mutation: schema.RegisterUserMutation,
      variables: {
        username,
        password,
        group,
      },
    })
  }

  async updateUser({ username, password }: { username: string; password: string }) {
    return await this.gqlClient.mutate({
      mutation: schema.UpdateUserMutation,
      variables: {
        username,
        password,
      },
    })
  }

  async deleteUser(username: string) {
    return await this.gqlClient.mutate({
      mutation: schema.DeleteUserMutation,
      variables: {
        username,
      },
    })
  }

  async fetchAllProducts(merchantId: MerchantId, category: ProductCategories) {
    const allProducts: ProductType[] = []

    const { data } = await this.productsV2(0, "", category, merchantId)

    const firstPageItems = data?.productsV2?.items || []
    allProducts.push(...firstPageItems)

    const totalPages = data?.productsV2?.pageInfo?.pageQty || 1

    if (totalPages <= 1) return allProducts

    const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 1)
    const pageRequests = remainingPages.map((page) =>
      this.productsV2(page, "", category, merchantId)
    )

    const responses = await Promise.all(pageRequests)

    responses.forEach(({ data }) => {
      const items = data?.productsV2?.items || []
      allProducts.push(...items)
    })

    return allProducts
  }

  async loginTiendanube(token: string): Promise<LoginAccessResponse | false> {
    let responseJson:
      | Awaited<ReturnType<typeof this.gqlClient.mutate<{ getAccess?: LoginAccessResponse }>>>
      | false
    try {
      responseJson = await this.gqlClient.mutate<{ getAccess?: LoginAccessResponse }>({
        mutation: schema.loginFromMiddle,
        variables: {
          token,
        },
      })
    } catch (e) {
      responseJson = false
    }

    if (!responseJson || !responseJson.data?.getAccess || responseJson.errors) return false

    return responseJson.data.getAccess
  }

  logout() {
    const isCommonUser = this.userType === IntegrationType.COMMON
    const loginRedirectUrl = `${window.location.origin}/login`
    clearTiendanubeToken()
    store.dispatch(clearBillingState())
    store.dispatch(clearUserInfo())
    store.dispatch(clearProductSlice())
    queryClient.clear()
    this.changeAwsTokenAndUserType(null, IntegrationType.COMMON)

    if (isCommonUser) {
      const clerk = (window as ClerkWindow).Clerk
      if (clerk?.signOut) {
        void clerk.signOut({ redirectUrl: loginRedirectUrl })
        return
      }
    }

    window.location.href = loginRedirectUrl
  }

  logoutUserExpired() {
    clearTiendanubeToken()
    store.dispatch(clearBillingState())
    store.dispatch(userExpired())
    store.dispatch(clearProductSlice())
    queryClient.clear()
    this.changeAwsToken(null)
  }

  userDetails() {
    const { username, groups } = store.getState().user.userInfo
    if (!username) return {}

    return {
      sub: username,
      username,
      groups,
    }
  }

  async userBrand() {
    const { username } = store.getState().user.userInfo
    if (!username) throw new Error("User does not exist")

    const { data: brand } = await this.brandById("", username)
    return brand as { name: string }
  }

  async listUsers() {
    const response = await this.gqlClient.mutate({
      mutation: schema.ListUsersMutation,
    })

    return {
      ...response,
      data: {
        ...response.data,
        listUsers: Array.isArray(response.data?.listUsers) ? response.data.listUsers : [],
      },
    }
  }

  async plans() {
    return fetch(`${this.legacyBillingEndpoint}/plans`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
  }

  async merchantById(merchantId: string) {
    return await this.gqlClient.query({
      query: schema.MerchantByIdQuery,
      variables: {
        merchantId,
      },
    })
  }

  async resolveMerchant(cognito: string) {
    return await this.gqlClient.query({
      query: schema.resolveMerchant,
      variables: {
        id: cognito,
      },
    })
  }

  async resolveMerchantByUserName(username: string) {
    return await this.gqlClient.query({
      query: schema.resolveMerchantByUName,
      variables: {
        cname: username,
      },
    })
  }

  async merchants() {
    const response = await this.gqlClient.query({
      query: schema.MerchantsQuery,
    })

    return {
      ...response,
      data: {
        ...response.data,
        merchants: Array.isArray(response.data?.merchants) ? response.data.merchants : [],
      },
    }
  }

  async products(page: number, partial: string, brand: string) {
    return await this.gqlClient.query({
      query: schema.ProductsQuery,
      variables: {
        page,
        partial,
        brand,
        adminMode: !brand,
      },
    })
  }

  async productsV2(
    page: number,
    partial: string,
    category: string,
    merchant: string,
    limit?: number
  ) {
    if (this.userType === IntegrationType.COMMON) {
      const response = await adminApi.listProducts({
        category,
        ...(limit ? { limit } : {}),
        merchantId: merchant,
        page,
        partial,
      })

      return {
        data: {
          productsV2: {
            items: response.items,
            pageInfo: {
              pageQty: response.pageCount,
            },
          },
        },
      }
    }

    return await this.gqlClient.query({
      query: schema.ProductsQueryV2,
      variables: {
        page,
        partial,
        category,
        merchant,
        ...(limit ? { limit } : {}),
      },
    })
  }

  async lookById(lookId: string) {
    return await this.gqlClient.query({
      query: schema.LookQuery,
      variables: {
        lookId,
      },
    })
  }

  async productByIdV2(productId: string): Promise<ProductV2> {
    const data: ProductByIdResult =
      this.userType === IntegrationType.COMMON
        ? {
            productV2: (await adminApi.getProduct(productId)) as ProductByIdResult["productV2"],
          }
        : (
            await this.gqlClient.query({
              query: schema.ProductQueryV2,
              variables: {
                productId,
              },
            })
          ).data

    let coverLeft: Cover | null = null,
      coverFront: Cover | null = null,
      coverRight: Cover | null = null

    if (data.productV2?.skus[0]?.vto.effectUrl) {
      const res = await convertGlassesZipToObject(data.productV2?.skus[0]?.vto.effectUrl)
      coverLeft = res.coverLeft ?? null
      coverFront = res.coverFront ?? null
      coverRight = res.coverRight ?? null
    }

    return {
      upc: "",
      id: data.productV2.id,
      name: data.productV2.name,
      cover: data.productV2.cover,
      externalId: data.productV2.externalId ?? undefined,
      brand: data.productV2.brand ?? "",
      brandSlug: data.productV2.brandSlug,
      type: data.productV2.type,
      categoryId: data.productV2.category.id,
      visible: data.productV2.visible ?? false,
      sku: data.productV2.skus[0].sku,
      region: data.productV2.skus[0].vto.region,
      coverage: data.productV2.skus[0].vto.coverage,
      finish: data.productV2.skus[0].vto.finish,
      url: data.productV2.skus[0].url,
      effectUrl: data.productV2.skus[0].vto.effectUrl,
      coverLeft,
      coverFront,
      coverRight,
      colors: data.productV2.skus.map((sku) => {
        return {
          color: sku.vto.color[0] !== "#" ? tintRgbTohex(sku.vto.color) : sku.vto.color,
          upc: sku.upc,
          sku: sku.sku,
          skuId: sku.id,
          externalId: sku.externalId ?? undefined,
          name: sku.name,
        }
      }),
      merchant: data.productV2.merchant ?? "",
    }
  }

  async getGlassesEditor(productId: string): Promise<GlassesEditorDto> {
    return await adminApi.getGlassesEditor(productId)
  }

  async createGlassesDraft(payload: {
    cover: string
    externalId?: string | null
    initialSkus?: GlassesEditorInitialSku[]
    name: string
    url?: string | null
  }): Promise<GlassesEditorDto> {
    return await adminApi.createGlassesDraft(payload)
  }

  async getGlassesDraft(draftId: string): Promise<GlassesEditorDto> {
    return await adminApi.getGlassesDraft(draftId)
  }

  async listGlassesDrafts(params: {
    merchantId: string
    partial?: string
  }): Promise<GlassesDraftListItem[]> {
    return await adminApi.listGlassesDrafts(params)
  }

  async getGlassesLimits(params: { merchantId: string }): Promise<GlassesLimits> {
    return await adminApi.getGlassesLimits(params)
  }

  async saveGlassesDraft(
    draftId: string,
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      url?: string | null
    }
  ): Promise<GlassesEditorDto> {
    return await adminApi.saveGlassesDraft(draftId, payload)
  }

  async deleteGlassesDraft(draftId: string): Promise<{ success: true }> {
    return await adminApi.deleteGlassesDraft(draftId)
  }

  async finalizeGlassesDraft(
    draftId: string,
    payload: {
      cover: string
      effectUrl?: string | null
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      url?: string | null
      visible: boolean
    }
  ): Promise<GlassesEditorDto> {
    return await adminApi.finalizeGlassesDraft(draftId, payload)
  }

  async saveGlassesEditor(
    productId: string,
    payload: {
      cover: string
      effectUrl?: string | null
      externalId?: string | null
      name: string
      url?: string | null
      visible: boolean
    }
  ): Promise<GlassesEditorDto> {
    return await adminApi.saveGlassesEditor(productId, payload)
  }

  async createGlassesSourceImageUploadUrl(payload: { contentType: string }) {
    return await adminApi.createGlassesSourceImageUploadUrl(payload)
  }

  async startGlassesDraftDigitization(
    draftId: string,
    payload: {
      cover: string
      externalId?: string | null
      initialSkus?: GlassesEditorInitialSku[]
      name: string
      sourceImageKeys: string[]
      url?: string | null
    }
  ) {
    return await adminApi.startGlassesDraftDigitization(draftId, payload)
  }

  async startInitialGlassesDraftDigitization(payload: {
    cover: string
    externalId?: string | null
    initialSkus?: GlassesEditorInitialSku[]
    name: string
    sourceImageKeys: string[]
    url?: string | null
  }) {
    return await adminApi.startInitialGlassesDraftDigitization(payload)
  }

  async startGlassesDigitization(payload: {
    cover: string
    externalId?: string | null
    initialSkus?: GlassesEditorInitialSku[]
    name: string
    productId?: string | null
    sourceImageKeys: string[]
    url?: string | null
  }) {
    return await adminApi.startGlassesDigitization(payload)
  }

  async brands(): Promise<Brand[]> {
    const data = await this.gqlClient.query({
      query: schema.BrandsQuery,
    })
    return data?.data?.brands || []
  }

  async brandById(id: string, sub: string) {
    return await this.gqlClient.query({
      query: schema.BrandByIdQuery,
      variables: {
        id,
        sub,
      },
    })
  }

  async looks() {
    return await this.gqlClient.query({
      query: schema.AllLooksQuery,
    })
  }

  async saveLook(look: any) {
    const selected: any[] = []

    if (look.products.selectedSkus)
      look.products.selectedSkus.forEach((sku: any) => {
        selected.push({
          sku: sku.id,
          product: sku.productId,
        })
      })

    let cover = look.cover

    if (look.file) {
      const {
        data: { authorizeCDN },
      } = await this.authorizeCDN()

      cover = await this.uploadCDN({
        body: look.file,
        name: look.file.name,
        type: look.file.type,
        jwt: authorizeCDN,
      })
    }

    return await this.gqlClient.mutate({
      mutation: schema.SaveLookMutation,
      variables: {
        lookId: look.id,
        cover,
        name: look.name,
        products: selected,
      },
    })
  }

  async deleteLook(lookId: string) {
    return await this.gqlClient.mutate({
      mutation: schema.DeleteLookMutation,
      variables: {
        lookId,
      },
    })
  }

  async deleteProduct(productId: string) {
    if (this.userType === IntegrationType.COMMON) {
      const response = await adminApi.deleteProduct(productId)
      return {
        data: {
          deleteProduct: response.success,
        },
      }
    }

    return await this.gqlClient.mutate({
      mutation: schema.DeleteProductMutation,
      variables: {
        productId,
      },
    })
  }

  cdnImgUrlName(productItem: { merchant?: string; merchantId?: string; file?: { name?: string } }) {
    return `imgs/products/${productItem.merchant || productItem.merchantId}/${
      productItem.file?.name ? productItem.file.name : new Date().getTime()
    }`
  }

  cdnEffectUrlName(productItem: { merchant?: string; merchantId?: string }) {
    return `effect/products/${
      productItem.merchant || productItem.merchantId
    }/effect_${new Date().getTime()}.zip`
  }

  async saveProductV2(productItem: ProductV2) {
    const productId = productItem.id
    const categoryId = Number(productItem.categoryId)
    const typeId = Number(productItem.typeId)

    let cover = productItem.cover

    if (productItem.file) {
      const {
        data: { authorizeCDN },
      } = await this.authorizeCDN()

      cover = await this.uploadCDN({
        body: productItem.file,
        name: this.cdnImgUrlName(productItem),
        type: productItem.file.type,
        jwt: authorizeCDN,
      })
    }

    let skus
    if (productItem.colors.length > 0)
      skus = productItem.colors.map(
        (objColor: {
          color: string
          upc: string
          sku: string
          skuId?: string
          externalId?: string
          name: string
        }) => ({
          id: objColor.skuId ?? "",
          externalId: objColor.externalId?.trim() || null,
          sku: objColor.sku,
          upc: objColor.upc,
          name: objColor.name,
          productId: productItem.id,
          url: productItem.url?.trim() || null,
          vto: {
            color: objColor.color.replaceAll(",", "").replaceAll(" ", ""),
            coverage: productItem.coverage,
            finish: productItem.finish,
            region: productItem.region,
          },
        })
      )
    else
      skus = [
        {
          id: "",
          sku: "",
          upc: "",
          name: "",
          productId: productItem.id,
          url: productItem.url?.trim() || null,
          vto: {
            color: "",
            coverage: productItem.coverage,
            finish: productItem.finish,
            region: productItem.region,
          },
        },
      ]

    const variables = {
      externalId: productItem.externalId?.trim() || null,
      name: productItem.name,
      merchant: productItem.merchant,
      id: productId,
      brand: productItem.brand,
      categoryId,
      typeId,
      cover,
      price: {
        amount: 0,
        currency: "",
      },
      skus,
    }

    if (this.userType === IntegrationType.COMMON) {
      const response = await adminApi.saveProduct(variables)
      return {
        data: {
          saveProductV2: response.id,
        },
      }
    }

    return await this.gqlClient.mutate({
      mutation: schema.SaveProductMutationV2,
      variables,
    })
  }

  async toggleProductVisibility(productId: string, visible: boolean) {
    if (this.userType === IntegrationType.COMMON) {
      const response = await adminApi.toggleProductVisibility(productId, visible)
      return {
        data: {
          toggleProductVisibility: response.success,
        },
      }
    }

    return await this.gqlClient.mutate({
      mutation: schema.ToggleProductVisibilityMutation,
      variables: {
        productId,
        visible,
      },
    })
  }

  async toggleLookVisibility(lookId: string, visible: boolean) {
    return await this.gqlClient.mutate({
      mutation: schema.ToggleLookVisibilityMutation,
      variables: {
        lookId,
        visible,
      },
    })
  }

  async saveBrand(brand: Brand) {
    return await this.gqlClient.mutate({
      mutation: schema.SaveBrandMutation,
      variables: {
        brandId: brand.id || "",
        slug: brand.slug,
        name: brand.name,
        sub: brand.sub,
      },
    })
  }

  async searchProducts(partial: string) {
    return await this.gqlClient.query({
      query: schema.SearchProductsQuery,
      variables: {
        partial,
      },
    })
  }

  async updateUICustomization({
    merchantId,
    data,
    mainLogo,
    shortLogo,
  }: {
    merchantId: string
    data: Record<string, string>
    mainLogo?: string
    shortLogo?: string
  }) {
    if (mainLogo === "" || shortLogo === "") {
      const {
        data: { authorizeCDN },
      } = await this.authorizeCDN()
      if (mainLogo === "")
        await this.deleteCDN({ name: `merchants/${merchantId}/logo.png`, jwt: authorizeCDN })

      if (shortLogo === "")
        await this.deleteCDN({ name: `merchants/${merchantId}/short-logo.png`, jwt: authorizeCDN })
    }
    const payload = {
      merchantId,
      data: JSON.stringify(data),
      mainLogo: mainLogo || "",
      shortLogo: shortLogo || "",
    }

    if (this.userType === IntegrationType.COMMON) {
      const response = await adminApi.saveUiCustomization(payload)
      return {
        data: {
          saveStyles: response.success,
        },
      }
    }

    return await this.gqlClient.mutate({
      mutation: schema.SaveStylesMutation,
      variables: payload,
    })
  }

  async getUICustomization(merchantId: string) {
    if (this.userType === IntegrationType.COMMON) {
      const response = await adminApi.getUiCustomization(merchantId)
      return {
        data: {
          getStyles: response.styles,
        },
      }
    }

    return await this.gqlClient.query({
      query: schema.GetStylesQuery,
      variables: {
        merchantId,
      },
    })
  }

  async getTranslates() {
    const response = await this.gqlClient.query({
      query: schema.GetTranslates,
    })

    return {
      ...response,
      data: {
        ...response.data,
        loadLangConfigs: Array.isArray(response.data?.loadLangConfigs)
          ? response.data.loadLangConfigs
          : [],
      },
    }
  }

  async saveTranslates(data: string) {
    return await this.gqlClient.mutate({
      mutation: schema.SaveTranslates,
      variables: {
        data,
      },
    })
  }

  async listFonts() {
    return await this.gqlClient.query({
      query: schema.ListFontsQuery,
    })
  }

  async merchantStatus(merchantId: string) {
    return await this.gqlClient.query({
      query: schema.MerchStatus,
      variables: {
        id: merchantId,
      },
    })
  }

  async uploadBulk(file: string, headers: string) {
    return await this.gqlClient.mutate({
      mutation: schema.BulkUpload,
      variables: {
        file,
        headers,
      },
    })
  }

  async getAnalytics(merchantId: string, days?: string, dateStart?: string, dateEnd?: string) {
    if (this.userType === IntegrationType.COMMON) {
      const response = await adminApi.getAnalytics({
        merchantId,
        days,
        dateStart,
        dateEnd,
      })
      return {
        data: {
          analytics: response.analytics,
        },
      }
    }

    return await this.gqlClient.query({
      query: schema.GetAnalytics,
      variables: {
        merchantID: merchantId,
        days,
        dateStart,
        dateEnd,
      },
    })
  }

  async getProductTranslations(productId: string, skuIds: string[]) {
    if (this.userType === IntegrationType.COMMON)
      return await adminApi.getProductTranslations(productId, skuIds)

    const { data } = await this.gqlClient.query({
      query: schema.GetProductTranslations,
      variables: {
        productId,
        skuIds,
      },
    })
    return {
      alternateName: data.alternateName || [],
      alternateSKUsName: data.alternateSKUsName || [],
    }
  }

  async saveProductTranslation(
    productId: string,
    merchantId: string,
    translations: Array<{ code: string; name: string; description?: string }>
  ) {
    const data = JSON.stringify(translations)
    if (this.userType === IntegrationType.COMMON) {
      const response = await adminApi.saveProductTranslations(productId, {
        data,
        merchantId,
      })
      return {
        data: {
          saveI8N: response.success,
        },
      }
    }

    return await this.gqlClient.mutate({
      mutation: schema.SaveProductI8NFromAdmin,
      variables: {
        Id: productId,
        merchantID: merchantId,
        data,
      },
    })
  }

  async saveSKUTranslation(
    skuId: string,
    merchantId: string,
    translations: Array<{ code: string; name: string; description?: string }>,
    productId?: string
  ) {
    const data = JSON.stringify(translations)
    if (this.userType === IntegrationType.COMMON) {
      const scopedProductId = productId?.trim()
      if (!scopedProductId) throw new Error("productId is required to save SKU translations")

      const response = await adminApi.saveSkuTranslations(skuId, {
        data,
        merchantId,
        productId: scopedProductId,
      })
      return {
        data: {
          saveI8N: response.success,
        },
      }
    }

    return await this.gqlClient.mutate({
      mutation: schema.SaveSKUI8NFromAdmin,
      variables: {
        Id: skuId,
        merchantID: merchantId,
        data,
      },
    })
  }
}

const tintVTOServiceCommon = new TintVTOService(IntegrationType.COMMON)
export const getTintVTOService = () => tintVTOServiceCommon

export const tintVTOService = getTintVTOService()
